/**
 * The `ShellMount` React context — `useShellMount()` + `useShellMountSnapshot()`
 * (frontend.md §3.2).
 *
 * `<AppShell>` provides the single `ShellMount` instance. Two consumer hooks:
 *  - `useShellMount()` — the registry handle, for calling `register*`. Agent 9's editor /
 *    chat entry points use this (in an effect, registering on mount, unregistering on
 *    unmount via the returned thunk).
 *  - `useShellMountSnapshot()` — subscribes to registry changes via `useSyncExternalStore`
 *    so a shell component (`<NavSidebar>`, `<CommandPalette>`, `<GlobalStatusBar>`,
 *    `<RouteOutlet>`) re-renders when something registers/unregisters.
 */

import { createContext, useContext, useSyncExternalStore } from 'react';
import type { ShellMount } from './ShellMount';

const ShellMountContext = createContext<ShellMount | null>(null);

export const ShellMountProvider = ShellMountContext.Provider;

/** The registry handle — for `register*` calls (Agent 9's mount path, the surfaces' seeding). */
export function useShellMount(): ShellMount {
  const mount = useContext(ShellMountContext);
  if (mount === null) {
    throw new Error('useShellMount must be used within an <AppShell> ShellMountProvider');
  }
  return mount;
}

/**
 * Subscribe to the registry — returns the same `ShellMount`, but re-renders the caller
 * whenever a registration changes. Shell components that *render* the registries use this.
 */
export function useShellMountSnapshot(): ShellMount {
  const mount = useShellMount();
  // The mount object identity is stable; we use `useSyncExternalStore` purely for the
  // change notification. The snapshot returns a monotonically-bumped token so React
  // re-renders; the component then reads `mount.getNavItems()` etc. fresh.
  useSyncExternalStore(
    (onChange) => mount.subscribe(onChange),
    () => registryVersion,
    () => registryVersion,
  );
  return mount;
}

// A module-level version counter bumped on every notify — `useSyncExternalStore`'s
// `getSnapshot` must return a value that changes when the store changes.
let registryVersion = 0;

/**
 * Wire the version counter to a `ShellMount` instance. `<AppShell>` calls this once with
 * its mount so `useShellMountSnapshot` sees a changing snapshot. (Kept out of
 * `createShellMount` so that module stays React-free / unit-testable.)
 */
export function bindRegistryVersionCounter(mount: ShellMount): void {
  mount.subscribe(() => {
    registryVersion += 1;
  });
}
